import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ServiceOrdersService } from '../../../services/service-orders.service';
import { ServiceOrderLogsService, ServiceOrderLog } from '../../../services/service-order-logs.service';
import {
  ServiceOrderWorkSection,
  ServiceOrderWorkSectionItem,
  ServiceOrderWorkSectionsService,
  ServiceOrderWorkSectionService as WorkSectionServiceLine
} from '../../../services/service-order-work-sections.service';
import { ServiceOrder } from '../../../models/ServiceOrder';

export interface HistoricoEventoView {
  tipo: string;
  acao: string;
  usuarioNome: string;
  dataHora: string;
  detalhes?: string;
  statusAnterior?: string;
  statusNovo?: string;
  setorAtual?: string;
}

@Component({
  selector: 'app-service-order-consulta-view-modal',
  templateUrl: './service-order-consulta-view-modal.component.html',
  styleUrls: ['./service-order-consulta-view-modal.component.css']
})
export class ServiceOrderConsultaViewModalComponent implements OnChanges {
  @Input() show = false;
  @Input() serviceOrderId: number | null = null;
  @Output() closeModal = new EventEmitter<void>();

  serviceOrder: ServiceOrder | null = null;
  historico: HistoricoEventoView[] = [];
  workSections: ServiceOrderWorkSection[] = [];
  loading = false;
  error = '';
  servicosExpanded = false;
  historicoExpanded = false;

  constructor(
    private serviceOrdersService: ServiceOrdersService,
    private serviceOrderLogsService: ServiceOrderLogsService,
    private serviceOrderWorkSectionsService: ServiceOrderWorkSectionsService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const showChanged = !!changes['show'];
    const idChanged = !!changes['serviceOrderId'];

    if (showChanged || idChanged) {
      if (this.show && this.serviceOrderId) {
        this.loadDetails();
      } else if (!this.show) {
        this.resetState();
      }
    }
  }

  close(): void {
    this.closeModal.emit();
  }

  toggleServicosSection(): void {
    this.servicosExpanded = !this.servicosExpanded;
  }

  toggleHistoricoSection(): void {
    this.historicoExpanded = !this.historicoExpanded;
  }

  private loadDetails(): void {
    const id = Number(this.serviceOrderId);
    if (!Number.isFinite(id) || id <= 0) return;

    this.loading = true;
    this.error = '';
    this.serviceOrder = null;
    this.historico = [];
    this.workSections = [];

    forkJoin({
      os: this.serviceOrdersService.getById(id).pipe(catchError(() => of(null))),
      logs: this.serviceOrderLogsService.getByServiceOrderId(id, { pageNumber: 1, pageSize: 100 }).pipe(
        catchError(() => of({ items: [], pageNumber: 1, pageSize: 100, totalCount: 0, totalPages: 0, hasPreviousPage: false, hasNextPage: false }))
      ),
      sections: this.loadWorkSectionsWithDetails(id).pipe(catchError(() => of([] as ServiceOrderWorkSection[])))
    }).subscribe({
      next: ({ os, logs, sections }) => {
        if (!os) {
          this.error = 'Não foi possível carregar os dados da ordem de serviço.';
          this.loading = false;
          return;
        }
        this.serviceOrder = os;
        this.historico = this.mapLogsToHistorico(logs.items || []);
        this.workSections = sections;
        this.loading = false;
      },
      error: () => {
        this.error = 'Erro ao carregar os dados da ordem de serviço.';
        this.loading = false;
      }
    });
  }

  private resetState(): void {
    this.serviceOrder = null;
    this.historico = [];
    this.workSections = [];
    this.loading = false;
    this.error = '';
    this.servicosExpanded = false;
    this.historicoExpanded = false;
  }

  private loadWorkSectionsWithDetails(serviceOrderId: number) {
    return this.serviceOrderWorkSectionsService.getByServiceOrderId(serviceOrderId).pipe(
      switchMap((sections) => {
        if (sections.length === 0) {
          return of([] as ServiceOrderWorkSection[]);
        }

        const requests = sections.map((section) => {
          const items$ = section.items?.length
            ? of(section.items)
            : this.serviceOrderWorkSectionsService.getItemsByWorkSectionId(section.id).pipe(
                catchError(() => of([] as ServiceOrderWorkSectionItem[]))
              );
          const services$ = section.services?.length
            ? of(section.services)
            : this.serviceOrderWorkSectionsService.getServicesByWorkSectionId(section.id).pipe(
                catchError(() => of([] as WorkSectionServiceLine[]))
              );

          return forkJoin({ items: items$, services: services$ }).pipe(
            map(({ items, services }) => ({ ...section, items, services }))
          );
        });

        return forkJoin(requests);
      })
    );
  }

  getSectionServices(section: ServiceOrderWorkSection): WorkSectionServiceLine[] {
    return section.services || [];
  }

  getSectionItems(section: ServiceOrderWorkSection): ServiceOrderWorkSectionItem[] {
    return section.items || [];
  }

  formatarValor(value: number | null | undefined): string {
    const n = Number(value);
    if (!Number.isFinite(n)) return 'R$ 0,00';
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  private mapLogsToHistorico(logs: ServiceOrderLog[]): HistoricoEventoView[] {
    return [...logs]
      .sort((left, right) => {
        const leftTime = new Date(left.createdAt || 0).getTime();
        const rightTime = new Date(right.createdAt || 0).getTime();
        return rightTime - leftTime;
      })
      .map((log) => ({
        tipo: this.resolveHistoricoTipo(log),
        acao: log.action || log.description || 'Movimentação da OS',
        usuarioNome: log.userName || 'Usuário Sistema',
        dataHora: log.createdAt || new Date().toISOString(),
        detalhes: log.details || log.description || '',
        statusAnterior: log.oldStatus || undefined,
        statusNovo: log.newStatus || undefined,
        setorAtual: log.department || undefined
      }));
  }

  private resolveHistoricoTipo(log: ServiceOrderLog): string {
    const content = `${log.action || ''} ${log.description || ''} ${log.details || ''}`.toLowerCase();

    if (log.oldStatus || log.newStatus || content.includes('status')) return 'mudanca_status';
    if (content.includes('direcion')) return 'direcionamento';
    if (content.includes('encerr') || content.includes('conclu')) return 'conclusao';
    if (content.includes('cancel')) return 'cancelamento';
    if (content.includes('edit') || content.includes('atualiz')) return 'edicao';
    if (content.includes('cria') || content.includes('abertura')) return 'criacao';

    return 'outros';
  }

  formatarDataHora(dataHora: string): string {
    const date = new Date(dataHora);
    if (isNaN(date.getTime())) return '-';
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const ano = date.getFullYear();
    const hora = String(date.getHours()).padStart(2, '0');
    const minuto = String(date.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} às ${hora}:${minuto}`;
  }

  formatarData(data: string | Date | undefined): string {
    if (!data) return '-';
    const date = new Date(data);
    if (isNaN(date.getTime())) return '-';
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const ano = date.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  getTipoOSNome(): string {
    if (!this.serviceOrder) return '-';
    return this.serviceOrder.configOrder?.typeOS || this.serviceOrder.tipoOS || this.serviceOrder.tipo || '-';
  }

  getSetorAtual(): string {
    if (!this.serviceOrder) return '-';
    if (this.serviceOrder.historico?.length) {
      const ultimo = [...this.serviceOrder.historico]
        .reverse()
        .find(h => h.tipo === 'direcionamento' && h.setorDestino);
      if (ultimo?.setorDestino) return ultimo.setorDestino;
    }
    return this.serviceOrder.setorAbertura || this.serviceOrder.unidadeNome || '-';
  }

  getIconeEvento(tipo: string): string {
    const icones: Record<string, string> = {
      criacao: 'fas fa-plus-circle',
      edicao: 'fas fa-edit',
      mudanca_status: 'fas fa-exchange-alt',
      direcionamento: 'fas fa-share',
      atribuicao: 'fas fa-user-tag',
      conclusao: 'fas fa-check-circle',
      cancelamento: 'fas fa-times-circle',
      outros: 'fas fa-info-circle'
    };
    return icones[tipo] || 'fas fa-circle';
  }

  getCorEvento(tipo: string): string {
    const cores: Record<string, string> = {
      criacao: '#28a745',
      edicao: '#ffc107',
      mudanca_status: '#17a2b8',
      direcionamento: '#007bff',
      atribuicao: '#fd7e14',
      conclusao: '#28a745',
      cancelamento: '#dc3545',
      outros: '#6c757d'
    };
    return cores[tipo] || '#6c757d';
  }
}
